/**
 * EficienciaPage — Bloque 4 · Reporte de lag comercial (registro vs voucher).
 *
 * Ranking por comercial de los últimos 7 días (configurable). Muestra:
 *   - Resumen del período (verde/amarillo/rojo/justificado + lag agregado)
 *   - Ranking por comercial: pct verde, pct rojo, lag mediano, trend vs semana anterior
 *   - Tabla de reservas rojas del período (accionable)
 *
 * Botón "PDF" descarga el mismo reporte formateado desde /reportes/lag-comerciales.pdf
 *
 * Endpoint: GET /reportes/lag-comerciales
 */
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardContent, TextField, IconButton, Chip, Button,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  CircularProgress, Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { API_BASE, getAuthHeaders } from '../booking/shared/format';

// Últimos 7 días por defecto (hoy - 6d hasta hoy)
function _hoyIso() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
function _menosDias(iso, n) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() - n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

const _empresa = () => localStorage.getItem('empresa_id') || localStorage.getItem('company_id') || '1';

export default function EficienciaPage() {
  const [hasta, setHasta] = useState(_hoyIso());
  const [desde, setDesde] = useState(_menosDias(_hoyIso(), 6));

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['reporte-lag', desde, hasta],
    queryFn: async () => {
      const url = `${API_BASE}/reportes/lag-comerciales?empresa_id=${_empresa()}&desde=${desde}&hasta=${hasta}`;
      const r = await fetch(url, { headers: getAuthHeaders() });
      if (!r.ok) throw new Error(`Error ${r.status}`);
      return r.json();
    },
    staleTime: 60_000,
  });

  const descargarPDF = () => {
    const url = `${API_BASE}/reportes/lag-comerciales.pdf?empresa_id=${_empresa()}&desde=${desde}&hasta=${hasta}`;
    window.open(url, '_blank');
  };

  const preset = (dias) => {
    const h = _hoyIso();
    setHasta(h);
    setDesde(_menosDias(h, dias - 1));
  };

  const resumen = data?.resumen || {};
  const comerciales = data?.comerciales || [];
  const rojas = data?.reservas_rojas || [];

  const semColor = (sem) => ({
    verde:       { bg: '#dcfce7', color: '#15803d' },
    amarillo:    { bg: '#fef3c7', color: '#a16207' },
    rojo:        { bg: '#fee2e2', color: '#b91c1c' },
    justificado: { bg: '#e0e7ff', color: '#3730a3' },
  }[sem] || { bg: '#f1f5f9', color: '#64748b' });

  const trendIcon = (t) => {
    if (t === '↑') return <TrendingUpIcon sx={{ fontSize: 16, color: '#b91c1c' }} />;
    if (t === '↓') return <TrendingDownIcon sx={{ fontSize: 16, color: '#15803d' }} />;
    return <TrendingFlatIcon sx={{ fontSize: 16, color: '#64748b' }} />;
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mb: 2,
                  flexDirection: { xs: 'column', sm: 'row' } }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 800, color: '#0f172a' }}>
            Lag de registro · por comercial
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#64748b' }}>
            Días entre voucher/pago y carga de la reserva · umbrales lag 0-1d oportuna · 2-3d rezago · &gt;3d retraso
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField type="date" size="small" value={desde} onChange={e => setDesde(e.target.value)}
            InputProps={{ startAdornment: <CalendarTodayIcon sx={{ fontSize: 16, color: '#94a3b8', mr: 0.5 }} /> }}
            sx={{ minWidth: 150 }} />
          <Typography sx={{ color: '#64748b' }}>→</Typography>
          <TextField type="date" size="small" value={hasta} onChange={e => setHasta(e.target.value)}
            sx={{ minWidth: 150 }} />
          <Button size="small" onClick={() => preset(7)} sx={{ textTransform: 'none' }}>7d</Button>
          <Button size="small" onClick={() => preset(15)} sx={{ textTransform: 'none' }}>15d</Button>
          <Button size="small" onClick={() => preset(30)} sx={{ textTransform: 'none' }}>30d</Button>
          <IconButton onClick={refetch} disabled={isFetching} size="small">
            <RefreshIcon sx={{ ...(isFetching && { animation: 'spin 1s linear infinite' }) }} />
          </IconButton>
          <Button variant="outlined" size="small" startIcon={<PictureAsPdfIcon />} onClick={descargarPDF}
            sx={{ textTransform: 'none' }}>PDF</Button>
        </Box>
      </Box>

      {isLoading || !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* Resumen del período */}
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>
                  Resumen del período · {data.periodo.dias} días
                </Typography>
                <Chip label={`${resumen.pct_al_dia}% cargadas oportunamente`} size="small"
                  sx={{ bgcolor: resumen.pct_al_dia >= 80 ? '#dcfce7' :
                                  resumen.pct_al_dia >= 50 ? '#fef3c7' : '#fee2e2',
                        color:   resumen.pct_al_dia >= 80 ? '#15803d' :
                                  resumen.pct_al_dia >= 50 ? '#a16207' : '#b91c1c',
                        fontWeight: 800, fontSize: 12 }} />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 1, mb: 1.5 }}>
                {[
                  { label: 'Evaluables',    valor: resumen.total_evaluables, color: '#0f172a', bg: '#f1f5f9' },
                  { label: '🟢 Oportunas',  valor: resumen.verde,       color: '#15803d', bg: '#dcfce7' },
                  { label: '🟡 Rezagadas',  valor: resumen.amarillo,    color: '#a16207', bg: '#fef3c7' },
                  { label: '🔴 Retrasadas', valor: resumen.rojo,        color: '#b91c1c', bg: '#fee2e2' },
                  { label: '🟪 Justificadas', valor: resumen.justificado, color: '#3730a3', bg: '#e0e7ff' },
                  { label: 'Lag mediano',   valor: `${resumen.lag_mediano_dias}d`, color: '#0f172a', bg: '#f1f5f9' },
                ].map(t => (
                  <Box key={t.label} sx={{ p: 1.25, bgcolor: t.bg, borderRadius: 1.5, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: t.color, lineHeight: 1 }}>
                      {t.valor}
                    </Typography>
                    <Typography sx={{ fontSize: 9, color: t.color, fontWeight: 700, mt: 0.5, textTransform: 'uppercase' }}>
                      {t.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Ranking por comercial */}
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a', mb: 1 }}>
                Ranking por comercial (peor primero)
              </Typography>
              {comerciales.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: '#64748b' }}>Sin datos en el período.</Typography>
              ) : (
                <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e2e8f0' }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Comercial</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11 }}>Reservas</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11 }}>🟢</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11 }}>🟡</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11 }}>🔴</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11 }}>🟪</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11 }}>% Al día</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11 }}>Lag med.</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11 }}>Trend</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comerciales.map(c => {
                        const bg = c.pct_rojo >= 20 ? '#fef2f2'
                                    : c.lag_mediano > 3 ? '#fefce8'
                                    : 'transparent';
                        return (
                          <TableRow key={c.nombre} hover sx={{ backgroundColor: bg }}>
                            <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{c.nombre}</TableCell>
                            <TableCell align="center" sx={{ fontSize: 12 }}>{c.total}</TableCell>
                            <TableCell align="center" sx={{ fontSize: 12, color: '#15803d', fontWeight: 700 }}>{c.verde}</TableCell>
                            <TableCell align="center" sx={{ fontSize: 12, color: '#a16207', fontWeight: 700 }}>{c.amarillo}</TableCell>
                            <TableCell align="center" sx={{ fontSize: 12, color: '#b91c1c', fontWeight: 700 }}>{c.rojo}</TableCell>
                            <TableCell align="center" sx={{ fontSize: 12, color: '#3730a3', fontWeight: 700 }}>{c.justificado}</TableCell>
                            <TableCell align="center" sx={{ fontSize: 12, fontWeight: 700,
                                          color: c.pct_verde >= 80 ? '#15803d' : c.pct_verde >= 50 ? '#a16207' : '#b91c1c' }}>
                              {c.pct_verde}%
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: 12, fontWeight: 700 }}>{c.lag_mediano}d</TableCell>
                            <TableCell align="center">
                              <Tooltip title={
                                c.lag_previo_mediano != null
                                  ? `Anterior: ${c.lag_previo_mediano}d`
                                  : 'Sin datos del período previo'
                              }>
                                <Box sx={{ display: 'inline-flex' }}>{trendIcon(c.trend)}</Box>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Reservas rojas del período */}
          {rojas.length > 0 && (
            <Card sx={{ mb: 2, borderRadius: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#b91c1c', mb: 1 }}>
                  Reservas retrasadas del período ({rojas.length})
                </Typography>
                <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #fecaca' }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#fef2f2' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#7f1d1d' }}>Código</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#7f1d1d' }}>Cliente</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#7f1d1d' }}>Comercial</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11, color: '#7f1d1d' }}>Voucher</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11, color: '#7f1d1d' }}>Registrada</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11, color: '#7f1d1d' }}>Lag</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rojas.map(r => (
                        <TableRow key={r.codigo} hover>
                          <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{r.codigo}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{r.cliente || '—'}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{r.comercial}</TableCell>
                          <TableCell align="center" sx={{ fontSize: 11, color: '#64748b' }}>{r.fecha_referencia || '—'}</TableCell>
                          <TableCell align="center" sx={{ fontSize: 11, color: '#64748b' }}>{r.fecha_registro || '—'}</TableCell>
                          <TableCell align="center">
                            <Chip label={`${r.dias}d`} size="small"
                              sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 800, fontSize: 11 }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}
